import PageContainer from "@/pages/containers/page-container";
import { useNotification, useRecommendation } from "@/hooks/use-notification";
import useUserInfo from "@/hooks/use-user-info";
import { QueryKeys } from "@/constants";

import type { Notification } from "@/model/notification";
import NotificationItem from "@/pages/components/notification/notification-item";
import NotificationSkeleton from "@/pages/components/notification/notification-skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import NotificationDetail from "@/pages/components/notification/notification-detail";
import RightSidebar from "./components/right-sidebar";
import { useMemo, useState } from "react";
import { htmlFromString } from "@/lib/string-util";
import NotificationAllSaveSwitcher, {
  NotificationAllSaveSwitcherType,
} from "./components/notification/notification-all-save-switcher";
import { useFeatureName } from "@/hooks/use-pageTitle";

const NotificationPage = () => {
  const featureName = useFeatureName();
  const userInfo = useUserInfo();

  const { data: notifications, isLoading: notificationsLoading } =
    useNotification(userInfo);
  const { data: recommendations, isLoading: recommendationsLoading } =
    useRecommendation(userInfo);

  //local-state
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const [selectedQueryKey, setSelectedQueryKey] = useState<
    QueryKeys.NOTIFICATIONS | QueryKeys.RECOMMENDATIONS
  >(QueryKeys.NOTIFICATIONS);
  const [open, setOpen] = useState<boolean>(false);

  const [notificationType, setNotificationType] =
    useState<NotificationAllSaveSwitcherType>(
      NotificationAllSaveSwitcherType.ALL,
    );

  const onSelectNotification = (notification: Notification) => {
    // TODO: Implement notification selection handler
    // console.log("Selected notification:", notification);
    setSelectedNotification(notification);
    setSelectedQueryKey(QueryKeys.NOTIFICATIONS);
    setOpen(true);
  };

  const onSelectRecommendation = (recommendation: Notification) => {
    setSelectedNotification(recommendation);
    setSelectedQueryKey(QueryKeys.RECOMMENDATIONS);
    // console.log("Selected notification:", recommendation);
    setOpen(true);
  };

  // Filter notifications and recommendations efficiently with useMemo
  const visibleNotifications = useMemo(() => {
    if (notificationType === NotificationAllSaveSwitcherType.ALL)
      return notifications;
    return notifications?.filter((item) => item.saveFlag);
  }, [notificationType, notifications]);

  const visibleRecommendations = useMemo(() => {
    if (notificationType === NotificationAllSaveSwitcherType.ALL)
      return recommendations;
    return recommendations?.filter((item) => item.saveFlag);
  }, [notificationType, recommendations]);

  if (notificationsLoading || recommendationsLoading) {
    return <NotificationSkeleton />;
  }

  return (
    <>
      <PageContainer title={featureName}>
        {/* tabs section */}
        <Tabs defaultValue="tab1" className="tabs">
          <div className="tabs-list-container">
            <TabsList className="tabs-list" style={{ padding: 0 }}>
              <TabsTrigger className="tabs-trigger" value="tab1">
                Notifications
              </TabsTrigger>
              <TabsTrigger className="tabs-trigger" value="tab2">
                Recommendations
              </TabsTrigger>
            </TabsList>
            <NotificationAllSaveSwitcher
              type={notificationType}
              setType={setNotificationType}
            />
          </div>
          {/* All or Saved notifications */}
          <TabsContent value="tab1" className="tabs-content">
            {visibleNotifications?.length === 0 ? (
              <div className="flex size-full items-center justify-center">
                <p className="text-sm text-gray-500">
                  {notificationType === NotificationAllSaveSwitcherType.ALL
                    ? "No notifications"
                    : "No saved notifications"}
                </p>
              </div>
            ) : (
              visibleNotifications?.map((item, index) => (
                <NotificationItem
                  key={`${item?.messageId}_${index}`}
                  notification={item}
                  onSelect={onSelectNotification}
                />
              ))
            )}
          </TabsContent>
          {/* All or Saved recommendations */}
          <TabsContent value="tab2" className="tabs-content">
            {visibleRecommendations?.length === 0 ? (
              <div className="flex size-full items-center justify-center">
                <p className="text-sm text-gray-500">
                  {notificationType === NotificationAllSaveSwitcherType.ALL
                    ? "No recommendations"
                    : "No saved recommendations"}
                </p>
              </div>
            ) : (
              visibleRecommendations?.map((item, index) => (
                <NotificationItem
                  key={`${item?.messageId}_${index}`}
                  notification={item}
                  onSelect={onSelectRecommendation}
                />
              ))
            )}
          </TabsContent>
        </Tabs>
      </PageContainer>
      {/* Right Sidebar Detail View */}
      {open && selectedNotification && (
        <RightSidebar
          title={`${selectedNotification?.subject ? htmlFromString(selectedNotification?.subject) : ""}`}
          isOpen={open}
          onClose={() => setOpen(false)}
        >
          <NotificationDetail
            notification={selectedNotification}
            onClose={() => setOpen(false)}
            queryKey={selectedQueryKey}
          />
        </RightSidebar>
      )}
    </>
  );
};

export default NotificationPage;
